import {useCallback, useEffect, useMemo, useReducer} from "react";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {useGetApplicationOfferDetailQuery} from "../api/application_lead_data_api";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {
    applicationModalStateReducer,
    IApplicationModalState,
    IApplicationModalStateData,
    initialApplicationModalState
} from "../reducers/application_modal_step_reducer";
import {ISentLeadActions, setDidSubmitOnMountAction} from "../reducers/lead_form_reducer";
import {resetMainLeadData, setLeadModalMainLeadType} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {ILeadModalLeadType} from "../types/lead/ILeadModalLeadType";
import {ApplicationSource, ApplicationSourceSection} from "../utils/form/application_form_source";
import {useInitialLeadFormValues} from "./use_initial_lead_form_values";
import {useLeadData} from "./use_lead_data";
import {useLeadRequiredParams} from "./use_lead_required_params";

export const useApplicationModalArray = () => {
    const dispatch = useAppDispatch();

    const {vendorId, offerId, propertyId} = useLeadRequiredParams();

    const {isModalOpen, source, sourceSection, customFormText, initialStep, forceClose} = useAppSelector((state) => state.mainLead);

    const leadData = useLeadData({
        vendorId,
        offerId: offerId || undefined,
        propertyId: propertyId || undefined,
        skipFetch: !isModalOpen
    });

    const {data: offerDetail} = useGetApplicationOfferDetailQuery({offerId: offerId!}, {skip: !isModalOpen || !offerId});
    const isAutoresponderEnabled = offerDetail?.configuration.is_autoresponder_enabled;

    const initialLeadFormValues = useInitialLeadFormValues({
        leadData,
        realEstateIds: {offerId: offerId || undefined, propertyId: propertyId || undefined, vendorId},
        resetValues: !isModalOpen,
        customFormText: customFormText || undefined
    });

    const isLeadDataLoading = !initialLeadFormValues || !leadData;
    const isAbroad = leadData?.offer?.detail?.region.country && leadData?.offer?.detail?.region.country !== 1; // Country.POLAND = 1

    // Build application modal state context from external data sources
    const applicationModalStateContext: IApplicationModalStateContext = useMemo(
        () => ({
            isMultileadEnabled: !!propertyId && !isAbroad,
            needsMarketingConsent: false, // Updated dynamically when lead response arrives via contextUpdates
            needsHomeMatchBasicPreferences: false,
            isAutoresponderEnabled: !!isAutoresponderEnabled,
            forceClose: !!forceClose
        }),
        [propertyId, isAbroad, isAutoresponderEnabled, forceClose]
    );

    const [modalState, dispatchModalState] = useReducer(applicationModalStateReducer, {
        ...initialApplicationModalState,
        ...(initialStep && {step: initialStep}),
        context: applicationModalStateContext
    } satisfies IApplicationModalState);

    // Send an event to the application modal state, optionally updating data and context atomically
    const sendModalEvent = useCallback(
        (event: ApplicationModalStateEvent, dataUpdates?: Partial<IApplicationModalStateData>, contextUpdates?: Partial<IApplicationModalStateContext>) => {
            dispatchModalState({type: "sendEvent", event, dataUpdates, contextUpdates});
        },
        []
    );

    // Sync application modal state context when external data changes
    useEffect(() => {
        dispatchModalState({type: "updateContext", context: applicationModalStateContext});
    }, [applicationModalStateContext]);

    // Determine the initial step when opening the modal
    useEffect(() => {
        if (!isModalOpen || isLeadDataLoading) {
            return;
        }

        const shouldUseFullOfferForm = propertyId && !isAbroad;
        const isSimpleFormStep =
            initialStep === ApplicationModalStep.APPLICATION_FORM_FULL_OFFER || initialStep === ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION;
        const isSidebarForm = initialStep === ApplicationModalStep.MULTILEAD && sourceSection === ApplicationSourceSection.SIDEBAR;

        let mainLeadType: ILeadModalLeadType;
        if (shouldUseFullOfferForm || isSimpleFormStep) {
            mainLeadType = "simple";
        } else if (isSidebarForm) {
            mainLeadType = "sidebar_form";
        } else {
            mainLeadType = "standard";
        }

        dispatch(setLeadModalMainLeadType(mainLeadType));

        if (initialStep) {
            return;
        }

        const step = shouldUseFullOfferForm ? ApplicationModalStep.APPLICATION_FORM_FULL_OFFER : ApplicationModalStep.APPLICATION_FORM_STANDARD;
        sendModalEvent({type: "OPEN", step});
    }, [isModalOpen, isLeadDataLoading]);

    // Close effect: when step transitions to CLOSE, reset Redux state
    useEffect(() => {
        if (modalState.step === ApplicationModalStep.CLOSE) {
            dispatch(resetMainLeadData());
            dispatch<ISentLeadActions>(setDidSubmitOnMountAction(false));
        }
    }, [modalState.step]);

    // Sync lead data to reducer
    useEffect(() => {
        if (leadData) {
            dispatchModalState({type: "updateData", data: {leadData}});
        }
    }, [leadData, isModalOpen]);

    // Verify that source and sourceSection are defined
    useEffect(() => {
        if (!source || !sourceSection) {
            notifyBugsnag("source or sourceSection is undefined", "useApplicationModalArray - source verification");
        }
    }, [source, sourceSection]);

    return {
        isOpened: isModalOpen,
        source: source as ApplicationSource,
        sourceSection: sourceSection as ApplicationSourceSection,
        modalState,
        sendModalEvent,
        leadData,
        initialLeadFormValues,
        customFormText
    };
};
import {useCallback, useEffect, useMemo, useReducer} from "react";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {useGetApplicationOfferDetailQuery} from "../api/application_lead_data_api";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {
    applicationModalStateReducer,
    IApplicationModalState,
    IApplicationModalStateData,
    initialApplicationModalState
} from "../reducers/application_modal_step_reducer";
import {ISentLeadActions, setDidSubmitOnMountAction} from "../reducers/lead_form_reducer";
import {resetMainLeadData, setLeadModalMainLeadType} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {ILeadModalLeadType} from "../types/lead/ILeadModalLeadType";
import {ApplicationSource, ApplicationSourceSection} from "../utils/form/application_form_source";
import {useInitialLeadFormValues} from "./use_initial_lead_form_values";
import {useLeadData} from "./use_lead_data";
import {useLeadRequiredParams} from "./use_lead_required_params";

export const useApplicationModalArray = () => {
    const dispatch = useAppDispatch();

    const {vendorId, offerId, propertyId} = useLeadRequiredParams();

    const {isModalOpen, source, sourceSection, customFormText, initialStep, forceClose} = useAppSelector((state) => state.mainLead);

    const leadData = useLeadData({
        vendorId,
        offerId: offerId || undefined,
        propertyId: propertyId || undefined,
        skipFetch: !isModalOpen
    });

    const {data: offerDetail} = useGetApplicationOfferDetailQuery({offerId: offerId!}, {skip: !isModalOpen || !offerId});
    const isAutoresponderEnabled = offerDetail?.configuration.is_autoresponder_enabled;

    const initialLeadFormValues = useInitialLeadFormValues({
        leadData,
        realEstateIds: {offerId: offerId || undefined, propertyId: propertyId || undefined, vendorId},
        resetValues: !isModalOpen,
        customFormText: customFormText || undefined
    });

    const isLeadDataLoading = !initialLeadFormValues || !leadData;
    const isAbroad = leadData?.offer?.detail?.region.country && leadData?.offer?.detail?.region.country !== 1; // Country.POLAND = 1

    // Build application modal state context from external data sources
    const applicationModalStateContext: IApplicationModalStateContext = useMemo(
        () => ({
            isMultileadEnabled: !!propertyId && !isAbroad,
            needsMarketingConsent: false, // Updated dynamically when lead response arrives via contextUpdates
            needsHomeMatchBasicPreferences: false,
            isAutoresponderEnabled: !!isAutoresponderEnabled,
            forceClose: !!forceClose
        }),
        [propertyId, isAbroad, isAutoresponderEnabled, forceClose]
    );

    const [modalState, dispatchModalState] = useReducer(applicationModalStateReducer, {
        ...initialApplicationModalState,
        ...(initialStep && {step: initialStep}),
        context: applicationModalStateContext
    } satisfies IApplicationModalState);

    // Send an event to the application modal state, optionally updating data and context atomically
    const sendModalEvent = useCallback(
        (event: ApplicationModalStateEvent, dataUpdates?: Partial<IApplicationModalStateData>, contextUpdates?: Partial<IApplicationModalStateContext>) => {
            dispatchModalState({type: "sendEvent", event, dataUpdates, contextUpdates});
        },
        []
    );

    // Sync application modal state context when external data changes
    useEffect(() => {
        dispatchModalState({type: "updateContext", context: applicationModalStateContext});
    }, [applicationModalStateContext]);

    // Determine the initial step when opening the modal
    useEffect(() => {
        if (!isModalOpen || isLeadDataLoading) {
            return;
        }

        const shouldUseFullOfferForm = propertyId && !isAbroad;
        const isSimpleFormStep =
            initialStep === ApplicationModalStep.APPLICATION_FORM_FULL_OFFER || initialStep === ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION;
        const isSidebarForm = initialStep === ApplicationModalStep.MULTILEAD && sourceSection === ApplicationSourceSection.SIDEBAR;

        let mainLeadType: ILeadModalLeadType;
        if (shouldUseFullOfferForm || isSimpleFormStep) {
            mainLeadType = "simple";
        } else if (isSidebarForm) {
            mainLeadType = "sidebar_form";
        } else {
            mainLeadType = "standard";
        }

        dispatch(setLeadModalMainLeadType(mainLeadType));

        if (initialStep) {
            return;
        }

        const step = shouldUseFullOfferForm ? ApplicationModalStep.APPLICATION_FORM_FULL_OFFER : ApplicationModalStep.APPLICATION_FORM_STANDARD;
        sendModalEvent({type: "OPEN", step});
    }, [isModalOpen, isLeadDataLoading]);

    // Close effect: when step transitions to CLOSE, reset Redux state
    useEffect(() => {
        if (modalState.step === ApplicationModalStep.CLOSE) {
            dispatch(resetMainLeadData());
            dispatch<ISentLeadActions>(setDidSubmitOnMountAction(false));
        }
    }, [modalState.step]);

    // Sync lead data to reducer
    useEffect(() => {
        if (leadData) {
            dispatchModalState({type: "updateData", data: {leadData}});
        }
    }, [leadData, isModalOpen]);

    // Verify that source and sourceSection are defined
    useEffect(() => {
        if (!source || !sourceSection) {
            notifyBugsnag("source or sourceSection is undefined", "useApplicationModalArray - source verification");
        }
    }, [source, sourceSection]);

    return {
        isOpened: isModalOpen,
        source: source as ApplicationSource,
        sourceSection: sourceSection as ApplicationSourceSection,
        modalState,
        sendModalEvent,
        leadData,
        initialLeadFormValues,
        customFormText
    };
};
